import { useEffect } from 'react';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import pushRoute from '@utils/pushRoute';
import Paper from 'aquilla/core/atoms/Paper';
import { segment } from '@actions/analytics';
import Button from 'aquilla/core/molecules/Button';
import { useIsMobile } from '@stores/AppCommonData';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';
import useOneLinkSmartScriptV2 from '@hooks/useOneLinkSmartScriptV2';
import { emptyObject } from '@constants/empty';
import { getAnimationPath } from '@utils/animationHelpers';
import LottieAnimation from '@base/LottieAnimation/LottieAnimation';
import { getIsDarkModeOn } from '@stores/selectors/darkModeStatusSelectors';
import { getIsLoggedIn } from '@api/selectors/auth';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import useDarkModeStatus from '@stores/useDarkModeStatus';
import segmentStorage from '@events/segmentStorage';
import Avatar from '@base/Avatar/Avatar';
import Image from '@base/FutureImage';
import useRatingInfo from '../useRatingInfo';
import { getIsCompeteHour, getUserLevel } from '../utils';
import { BANNER_BANNER_CLICKED, BANNER_BANNER_VIEWED } from '../constants';

const AnimationContainer = styled(LottieAnimation)`
  height: 20px;
  width: 50px;
  margin-right: ${Spacings.SPACING_6};
`;

const Wrapper = styled.div`
  margin-top: ${Spacings.SPACING_5B};
  margin-bottom: ${Spacings.SPACING_14B};
  grid-column: span 18;
`;

const Banner = styled(Paper)`
  padding: ${Spacings.SPACING_6B};
  height: 156px;
  margin-top: ${Spacings.SPACING_8B};
  border-radius: 8px;
  position: relative;
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_6B};
    height: 164px;
    border: 1px solid var(--color-divider);
    padding: ${Spacings.SPACING_4B};
    h4 {
      font-size: ${Fonts.H5};
      font-weight: ${Fonts.SEMIBOLD};
      line-height: 24px;
      max-width: 236px;
    }
  }
`;

const SubtextWrapper = styled.div`
  margin-top: ${Spacings.SPACING_6};
  height: 20px;
  display: flex;
  align-items: center;
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_2B};
  }
`;

const CTAWrapper = styled.div`
  margin-top: ${Spacings.SPACING_4B};
  height: 40px;
  display: flex;
  align-items: center;
`;

const PrimaryCTA = styled(Button)`
  margin-right: ${Spacings.SPACING_4B};
  background-color: ${({ $isDarkMode }) =>
    $isDarkMode ? '' : 'var(--color-i-blue-44)'};
  width: 124px;
  ${mediaQuery.uptoMobile} {
    margin-right: ${Spacings.SPACING_3B};
    background-color: ${({ $isDarkMode }) =>
      $isDarkMode ? '' : 'var(--color-base-fill)'};
    width: auto;
  }
`;

const SecondaryButton = styled(Button)`
  ${mediaQuery.uptoMobile} {
    width: 145px;
  }
`;

const AvatarWrapper = styled.div`
  position: absolute;
  top: 24px;
  right: 24px;
  width: 72px;
  height: 72px;
  ${mediaQuery.uptoMobile} {
    top: 16px;
    right: 16px;
    width: 48px;
    height: 48px;
  }
`;

const LevelIcon = styled.div`
  right: 0;
  bottom: 0;
  width: 22.5px;
  height: 22.5px;
  position: absolute;
  ${mediaQuery.uptoMobile} {
    width: 20px;
    height: 20px;
  }
`;

const StyledH4 = styled(Typography)`
  line-height: 30px;

  ${mediaQuery.uptoMobile} {
    font-weight: ${Fonts.SEMIBOLD};
    line-height: 24px;
  }
`;

const StyledAvatar = styled(Avatar)`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  ${mediaQuery.uptoMobile} {
    font-weight: 48px;
    line-height: 48px;
    width: 48px;
    height: 48px;
  }
`;

const AwarenessBanner = ({ block }) => {
  const {
    title,
    endTime,
    subTitle,
    startTime,
    userAvatar,
    userRating,
    liveTitle,
    count
  } = block?.data || emptyObject;

  const isMobile = useIsMobile();
  const {
    query: { goalSlug, goalUID }
  } = useRouter();
  const { levels } = useRatingInfo();
  const level = getUserLevel(userRating, levels);
  const isCompeteHour = getIsCompeteHour(startTime, endTime);
  const isDarkModeOn = useDarkModeStatus(getIsDarkModeOn);
  const appDownloadLink = useOneLinkSmartScriptV2();

  const isLoggedIn = useMyInfo(getIsLoggedIn);

  useEffect(() => {
    if (title) {
      segment.track(BANNER_BANNER_VIEWED, {
        banner_title: title,
        banner_type: 'Compete Awareness',
        goal_uid: goalUID
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isLoggedIn) {
    return null;
  }

  const handleCompeteNowClick = () => {
    segment.track(BANNER_BANNER_CLICKED, {
      cta_button: isMobile ? 'Download app' : 'Compete now',
      banner_title: title,
      goal_uid: goalUID,
      banner_type: 'Compete Awareness'
    });
    if (isMobile) {
      window.open(appDownloadLink, '_blank');
      return;
    }
    segmentStorage.setItem('lps', 'Goal Home');
    return pushRoute({
      pathname: '/goal/[goalSlug]/[goalUID]/compete/battlefield',
      query: {
        goalSlug,
        goalUID
      }
    });
  };

  const handleLearnMoreClick = () => {
    segment.track(BANNER_BANNER_CLICKED, {
      cta_button: 'Learn more',
      banner_title: title,
      goal_uid: goalUID,
      banner_type: 'Compete Awareness'
    });
    segmentStorage.setItem('lps', 'Goal Home');
    pushRoute({
      pathname: '/goal/[goalSlug]/[goalUID]/compete',
      query: {
        goalSlug,
        goalUID
      }
    });
  };

  const renderSubTitle = () => {
    if (subTitle) {
      return subTitle;
    }
    if (count) {
      return `${count} learners competing`;
    }
  };

  return (
    <Wrapper>
      <StyledH4 variant="h4">Compete</StyledH4>
      <Banner>
        <Typography variant="h4">
          {isCompeteHour ? liveTitle : title}
        </Typography>
        <SubtextWrapper>
          {(subTitle || count) && (
            <>
              <AnimationContainer
                src={getAnimationPath('competeJoiningMembers.json')}
                loop
              />
              <Typography variant="p2">{renderSubTitle()}</Typography>
            </>
          )}
        </SubtextWrapper>
        <CTAWrapper>
          <PrimaryCTA
            variant="primary"
            label={isMobile ? 'Download app' : 'Compete'}
            onClick={handleCompeteNowClick}
            $isDarkMode={isDarkModeOn}
          />
          <SecondaryButton
            label="Learn more"
            variant="secondary"
            onClick={handleLearnMoreClick}
          />
        </CTAWrapper>
        {userAvatar && (
          <AvatarWrapper>
            <div>
              <StyledAvatar src={userAvatar} alt="" objectFit="fill" />
            </div>

            <LevelIcon>
              {!!level?.strokedBadgeImageV1 && (
                <Image
                  src={level.strokedBadgeImageV1}
                  width={isMobile ? 20 : 22.5}
                  height={isMobile ? 20 : 22.5}
                  alt="badge"
                />
              )}
            </LevelIcon>
          </AvatarWrapper>
        )}
      </Banner>
    </Wrapper>
  );
};

export default AwarenessBanner;
